import { pipeline, env } from '@huggingface/transformers';

// Configure transformers.js to always download models
env.allowLocalModels = false;
env.useBrowserCache = false;

const MAX_IMAGE_DIMENSION = 1024;

function resizeImageIfNeeded(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, image: HTMLImageElement) {
  let width = image.naturalWidth;
  let height = image.naturalHeight;

  if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
    if (width > height) {
      height = Math.round((height * MAX_IMAGE_DIMENSION) / width);
      width = MAX_IMAGE_DIMENSION;
    } else {
      width = Math.round((width * MAX_IMAGE_DIMENSION) / height);
      height = MAX_IMAGE_DIMENSION;
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(image, 0, 0, width, height);
    return true;
  }

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(image, 0, 0);
  return false;
}

function sampleBorderColors(imageData: ImageData): number[][] {
  const colors: number[][] = [];
  const width = imageData.width;
  const height = imageData.height;
  const data = imageData.data;
  
  // Sample top and bottom edges
  for (let x = 0; x < width; x += 10) {
    // Top edge
    const topIdx = x * 4;
    colors.push([data[topIdx], data[topIdx + 1], data[topIdx + 2]]);
    
    // Bottom edge
    const bottomIdx = ((height - 1) * width + x) * 4;
    colors.push([data[bottomIdx], data[bottomIdx + 1], data[bottomIdx + 2]]);
  }
  
  // Sample left and right edges
  for (let y = 0; y < height; y += 10) {
    // Left edge
    const leftIdx = y * width * 4;
    colors.push([data[leftIdx], data[leftIdx + 1], data[leftIdx + 2]]);
    
    // Right edge
    const rightIdx = (y * width + width - 1) * 4;
    colors.push([data[rightIdx], data[rightIdx + 1], data[rightIdx + 2]]);
  }
  
  return colors;
}

function colorDistance(c1: number[], c2: number[]): number {
  return Math.sqrt(
    Math.pow(c1[0] - c2[0], 2) +
    Math.pow(c1[1] - c2[1], 2) +
    Math.pow(c1[2] - c2[2], 2)
  );
}

function cleanupCheckerboard(imageData: ImageData, threshold: number = 40): void {
  const borderColors = sampleBorderColors(imageData);
  const data = imageData.data;
  
  for (let i = 0; i < data.length; i += 4) {
    const pixelColor = [data[i], data[i + 1], data[i + 2]];
    
    // Check if this pixel is close to any border color
    let isBackground = false;
    for (const borderColor of borderColors) {
      if (colorDistance(pixelColor, borderColor) < threshold) {
        isBackground = true;
        break;
      }
    }
    
    if (isBackground) {
      data[i + 3] = 0; // Set alpha to 0 (fully transparent)
    }
  }
}

export const removeBackground = async (imageElement: HTMLImageElement): Promise<Blob> => {
  try {
    console.log('Starting background removal process...');
    const remover = await pipeline('image-segmentation', 'briaai/RMBG-1.4', {
      device: 'webgpu',
    });
    
    // Convert HTMLImageElement to canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) throw new Error('Could not get canvas context');
    
    // Resize image if needed and draw it to canvas
    const wasResized = resizeImageIfNeeded(canvas, ctx, imageElement);
    console.log(`Image ${wasResized ? 'was' : 'was not'} resized. Final dimensions: ${canvas.width}x${canvas.height}`);
    
    // Get image data as base64 PNG (lossless)
    const imageData = canvas.toDataURL('image/png', 1.0);
    console.log('Image converted to base64 PNG');
    
    // Process the image with the background removal model
    console.log('Processing with RMBG-1.4 model (this may take 30-60s on first run)...');
    const result = await remover(imageData);
    
    console.log('Background removal result:', result);
    
    if (!result || !Array.isArray(result) || result.length === 0 || !result[0].mask) {
      throw new Error('Invalid background removal result');
    }
    
    // Create a new canvas for the masked image
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = canvas.width;
    outputCanvas.height = canvas.height;
    const outputCtx = outputCanvas.getContext('2d');
    
    if (!outputCtx) throw new Error('Could not get output canvas context');
    
    // Draw original image
    outputCtx.drawImage(canvas, 0, 0);
    
    // Apply the mask
    const outputImageData = outputCtx.getImageData(
      0, 0,
      outputCanvas.width,
      outputCanvas.height
    );
    const data = outputImageData.data;
    const maskData = result[0].mask.data;
    
    // Apply mask to alpha channel (no inversion needed for RMBG)
    for (let i = 0; i < maskData.length; i++) {
      const alpha = Math.round(maskData[i] * 255);
      data[i * 4 + 3] = alpha;
    }
    
    outputCtx.putImageData(outputImageData, 0, 0);
    console.log('Mask applied successfully');
    
    // Post-process: cleanup checkerboard patterns
    const finalImageData = outputCtx.getImageData(0, 0, outputCanvas.width, outputCanvas.height);
    cleanupCheckerboard(finalImageData);
    outputCtx.putImageData(finalImageData, 0, 0);
    console.log('Checkerboard cleanup applied');
    
    // Convert canvas to blob
    return new Promise((resolve, reject) => {
      outputCanvas.toBlob(
        (blob) => {
          if (blob) {
            console.log('Successfully created final blob');
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        'image/png',
        1.0
      );
    });
  } catch (error) {
    console.error('Error removing background:', error);
    throw error;
  }
};

export const loadImage = (file: Blob): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};
